/**
 * ModulesFull — public marketing page covering every module on the
 * platform. Lives at /modules-full so we don't clobber the existing
 * /modules → EcosystemGateway alias.
 *
 * Eight modules organised into 4 pillars:
 *   I.   Sovereign Intelligence (Shura Nexus, AI Council, Iqra, Wujdan)
 *   II.  Capital + Pipeline (Opportunity CRM, Finance Engine, Approvals)
 *   III. Governance + Risk (ERM Kickstart, Compliance Threads, Audit)
 *   IV.  Ecosystem + Brand (Content Studio, Branding, Public Splash)
 *
 * Each pillar gets a card with: name, sub, 4 key features, who it's
 * for, screenshot/illustration placeholder, "explore →" link.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Network, Sparkles, BookOpen, Heart,
  Briefcase, Wallet, CheckSquare,
  ShieldCheck, MessageSquare, Activity,
  Megaphone, Palette, Globe,
  ArrowRight, Crown,
} from 'lucide-react';
import LivingBackground from '@/components/LivingBackground';
import PublicNavbar from '@/components/PublicNavbar';

const PILLARS = [
  {
    id: 'sovereign',
    name: 'Sovereign Intelligence',
    blurb: 'Twenty-six AI advisors that work for you and you alone. Every prompt redacted, every call receipted, every model swappable for your own Bedrock.',
    accent: '#AE52EA',
    modules: [
      { icon: Network,      title: 'Shura Nexus',        desc: 'Living force-directed graph connecting investors, startups, holdings, and incubators in 3D.' },
      { icon: Sparkles,     title: 'AI Council',         desc: 'Multi-persona deliberation: Yusuf (risk), Iqra (vision), Wujdan (ethics), and 23 others — in parallel.' },
      { icon: BookOpen,     title: 'Iqra Journal',       desc: 'AI-narrated journal of every decision, deal, and dissent. Weekly digest emailed automatically.' },
      { icon: Heart,        title: 'Wujdan Conscience',  desc: 'Ethics-first review layer. Refuses unsafe asks, surfaces moral conflicts before they cost you reputation.' },
    ],
  },
  {
    id: 'capital',
    name: 'Capital & Pipeline',
    blurb: 'Bloomberg-terminal-grade deal flow + finance for holding companies, family offices, and accelerators. Every dollar tracked, every commitment weighted, every decision logged.',
    accent: '#34D399',
    modules: [
      { icon: Briefcase,    title: 'Opportunity CRM',    desc: 'Weighted pipeline with conviction × ticket-size × stage. Lossless audit trail per deal.' },
      { icon: Wallet,       title: 'Amwāl Finance Engine', desc: 'Multi-entity rollups, currency-aware ledger, annual report PDF auto-generated on Jan 1.' },
      { icon: CheckSquare,  title: 'Approvals & Routing', desc: 'Threshold-based routing: term sheets > $1M, board memos, ERM upgrades. Email digests for approvers.' },
    ],
  },
  {
    id: 'governance',
    name: 'Governance & Risk',
    blurb: 'SEC-aligned ERM registry, compliance threading, and the full audit trail your auditors keep asking for. Built for the Gulf, friendly to FATF & QFCRA.',
    accent: '#F59E0B',
    modules: [
      { icon: ShieldCheck,  title: 'ERM Kickstart',      desc: 'Generate a SEC-grade Enterprise Risk Registry in 15 minutes via the AI ERM Kickstart wizard.' },
      { icon: MessageSquare,title: 'Compliance Threads', desc: 'Threaded chat between Compliance, Legal, and the Government persona. PII-redacted exports.' },
      { icon: Activity,     title: 'Sovereign-AI Privacy',  desc: 'Live PII redaction, trade-secret tokens, Sovereignty Score, provider policy matrix — all admin-visible.' },
    ],
  },
  {
    id: 'ecosystem',
    name: 'Ecosystem & Brand',
    blurb: 'Whitelabel every tenant. Multi-channel content engine. Brand customisation down to the living background. Your portfolio looks as luxe as your strategy.',
    accent: '#EC4899',
    modules: [
      { icon: Megaphone,    title: 'Content Studio',     desc: 'AI-drafted posts (LinkedIn, X, Insta, YouTube) with weighted approval routing + scheduled auto-publish.' },
      { icon: Palette,      title: 'Tenant Branding',    desc: 'Color pickers, animated living backgrounds, custom logos with auto-transparent halos. Per-tenant theme.' },
      { icon: Globe,        title: 'Ecosystem Gateway',  desc: 'Public splash page introducing each tenant in your network. Mobile-first, SEO-aware.' },
    ],
  },
];


export default function ModulesFull() {
  return (
    <div className="min-h-screen relative bg-[var(--ve-bg)] text-[var(--ve-fg)]">
      <LivingBackground />
      <PublicNavbar />
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 py-12 lg:py-20" data-testid="modules-full-page">

        {/* Hero */}
        <section className="mb-16">
          <div className="ve-eyebrow">[OPERATING SYSTEM · 13 MODULES · 4 PILLARS]</div>
          <h1 className="display text-5xl lg:text-6xl mt-3 leading-[1.05]">One platform. <span style={{ color: 'var(--brand-primary)' }}>Every</span> stage of the holding-company stack.</h1>
          <p className="mt-6 text-base lg:text-lg opacity-75 leading-relaxed max-w-3xl">
            Vigilant Entities replaces a stack of seven tools — CRM, ERM, finance, content, board portal, compliance, AI advisory — with one Sovereign-AI-powered operating system. Designed for holding companies, family offices, and accelerators that need Wall Street muscle without giving up Doha-grade sovereignty.
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-300 pal-mono">
            <Crown size={12}/> <span>Every module is Sovereign-AI by default — your queries stay yours.</span>
          </div>
        </section>

        {/* Pillars */}
        {PILLARS.map((p) => (
          <section key={p.id} data-testid={`pillar-${p.id}`} className="mb-16">
            <div className="flex items-baseline gap-3 mb-6">
              <span className="pal-mono text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded border" style={{ borderColor: `${p.accent}55`, color: p.accent }}>
                [{p.id.toUpperCase()}]
              </span>
              <h2 className="display text-3xl lg:text-4xl" style={{ color: p.accent }}>{p.name}</h2>
            </div>
            <p className="opacity-80 text-base max-w-3xl mb-6 leading-relaxed">{p.blurb}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {p.modules.map((m) => (
                <div key={m.title} className="ve-card p-5 hover:border-[var(--brand-primary)]/40 transition-colors" data-testid={`module-${m.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <div className="h-10 w-10 rounded-md flex items-center justify-center mb-3" style={{ background: `${p.accent}15`, color: p.accent }}>
                    <m.icon size={18}/>
                  </div>
                  <div className="font-bold text-base">{m.title}</div>
                  <p className="text-sm opacity-75 mt-1 leading-relaxed">{m.desc}</p>
                </div>
              ))}
            </div>
          </section>
        ))}

        {/* CTA */}
        <div className="mt-12 ve-card p-8 lg:p-12 text-center">
          <h2 className="display text-3xl lg:text-4xl">Built for operators who play the long game.</h2>
          <p className="mt-3 opacity-75 max-w-2xl mx-auto">Bedrock-grade sovereignty. Bloomberg-grade UI. Doha-grade hospitality. Pick the modules you need today — the rest are waiting when you grow into them.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/pricing" data-testid="modules-cta-pricing" className="ve-btn-primary inline-flex items-center gap-1.5">See pricing <ArrowRight size={13}/></Link>
            <Link to="/security" data-testid="modules-cta-security" className="px-5 py-2.5 rounded border border-white/20 text-sm font-bold uppercase tracking-[0.18em] hover:bg-white/5 transition-colors">Read about security</Link>
          </div>
        </div>

        <footer className="mt-16 pt-8 border-t border-white/5 flex items-center justify-between flex-wrap gap-3 text-[10px] uppercase tracking-[0.18em] opacity-50">
          <span>© Vigilant Entities · M3 Holdings · Doha</span>
          <div className="flex gap-4">
            <Link to="/modules-full" className="hover:opacity-80">Modules</Link>
            <Link to="/pricing"      className="hover:opacity-80">Pricing</Link>
            <Link to="/security"     className="hover:opacity-80">Security</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
